import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Phone, Mail, Facebook, Instagram, User, LogOut } from 'lucide-react';
import UnderConstructionBanner from './UnderConstructionBanner';
import LanguageSwitcher from './LanguageSwitcher';
import NewsletterSignup from './NewsletterSignup';
import { useAuth } from '../context/AuthContext';

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.destinations'), path: '/destinations' },
    { name: t('nav.travelPackages'), path: '/partners' },
    { name: t('nav.contact'), path: '/contact' },
    { name: t('nav.blog'), path: '/blog' },
  ];

  return (
    <div className="min-h-screen paper-texture">
      <UnderConstructionBanner />
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-cream/95 backdrop-blur-sm border-b border-maroon/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 gap-4">
            <Link to="/" className="flex items-center space-x-3 flex-shrink-0">
              <img 
                src="/logos/tamarin-dark.png" 
                alt="Chimba Travel" 
                className="h-16 sm:h-20"
                data-testid="header-logo"
              />
              <span className="font-heading font-bold text-maroon text-2xl sm:text-3xl leading-tight">Chimba Travel</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center space-x-1 flex-shrink-0">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-3 py-2 rounded-sm font-semibold transition-all duration-200 text-sm whitespace-nowrap ${
                    location.pathname === link.path
                      ? 'bg-maroon text-cream'
                      : 'text-maroon hover:bg-maroon/10'
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              {user ? (
                <>
                  <Link 
                    to="/account" 
                    className="hidden xl:flex items-center gap-2 px-4 py-2 text-maroon hover:bg-maroon/10 rounded-sm font-semibold text-sm"
                    data-testid="my-account-nav"
                  >
                    <User size={18} />
                    {t('nav.myAccount')}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="hidden xl:flex items-center gap-2 px-3 py-2 text-maroon/60 hover:text-coral hover:bg-coral/5 rounded-sm font-medium text-sm transition-colors"
                    data-testid="header-logout-btn"
                  >
                    <LogOut size={16} />
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <Link 
                  to="/login" 
                  className="hidden xl:flex items-center gap-2 px-4 py-2 text-maroon hover:bg-maroon/10 rounded-sm font-semibold text-sm"
                  data-testid="login-nav"
                >
                  <User size={18} />
                  {t('nav.login')}
                </Link>
              )}
              <Link 
                to="/consultation" 
                className="hidden xl:block btn-primary text-xs px-4 py-2 whitespace-nowrap"
                data-testid="book-consultation-nav"
              >
                {t('header.bookConsultation')}
              </Link>

              <button
                className="xl:hidden p-2 text-maroon"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="xl:hidden pb-4 space-y-2" data-testid="mobile-menu">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-2 rounded-sm font-semibold transition-all duration-200 ${
                    location.pathname === link.path
                      ? 'bg-maroon text-cream'
                      : 'text-maroon hover:bg-maroon/10'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              {user ? (
                <>
                  <Link
                    to="/account"
                    className="block px-4 py-2 rounded-sm font-semibold text-maroon hover:bg-maroon/10 flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <User size={18} />
                    My Account
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="w-full text-left px-4 py-2 rounded-sm font-semibold text-coral hover:bg-coral/5 flex items-center gap-2"
                    data-testid="mobile-logout-btn"
                  >
                    <LogOut size={18} />
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="block px-4 py-2 rounded-sm font-semibold text-maroon hover:bg-maroon/10 flex items-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <User size={18} />
                  Login / Register
                </Link>
              )}
              <Link
                to="/consultation"
                className="block px-4 py-2 rounded-sm bg-maroon text-cream font-bold text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                Book Consultation
              </Link>
            </nav>
          )}
        </div>
      </header>

      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-maroon text-cream mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <img 
                src="/logos/tamarin-light.png" 
                alt="Chimba Travel" 
                className="h-16 mb-2"
                data-testid="footer-logo"
              />
              <p className="font-heading font-bold text-cream text-xl mb-1">Chimba Travel</p>
              <p className="text-cream/60 text-sm italic">Where travel becomes connection</p>
            </div>
            
            <div>
              <h3 className="font-heading font-bold text-lg mb-4">{t('footer.quickLinks')}</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-cream/70 hover:text-cream transition-colors">{t('footer.aboutUs')}</Link></li>
                <li><Link to="/destinations" className="text-cream/70 hover:text-cream transition-colors">{t('footer.destinations')}</Link></li>
                <li><Link to="/partners" className="text-cream/70 hover:text-cream transition-colors">{t('footer.travelPackages')}</Link></li>
                <li><Link to="/contact" className="text-cream/70 hover:text-cream transition-colors">{t('footer.contact')}</Link></li>
                <li><Link to="/blog" className="text-cream/70 hover:text-cream transition-colors">{t('footer.blog')}</Link></li>
                <li><Link to="/faq" className="text-cream/70 hover:text-cream transition-colors">{t('footer.faq')}</Link></li>
                <li><Link to="/terms" className="text-cream/70 hover:text-cream transition-colors">{t('footer.terms')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-heading font-bold text-lg mb-4">{t('footer.contactInfo')}</h3>
              <ul className="space-y-2">
                <li className="flex items-start space-x-2">
                  <Phone size={16} className="mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-cream/50">Sales (Toll-Free)</p>
                    <a href="tel:+18335688090" className="text-cream/70 hover:text-cream underline transition-colors">
                      (833) 568-8090
                    </a>
                  </div>
                </li>
                <li className="flex items-start space-x-2">
                  <Phone size={16} className="mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-cream/50">Business Inquiries</p>
                    <a href="tel:+13618574637" className="text-cream/70 hover:text-cream underline transition-colors">
                      (361) 857-4637
                    </a>
                  </div>
                </li>
                <li className="flex items-start space-x-2">
                  <Mail size={16} className="mt-1 flex-shrink-0" />
                  <a href="mailto:hello@chimbatravel.com" className="text-cream/70 hover:text-cream underline transition-colors">
                    hello@chimbatravel.com
                  </a>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-heading font-bold text-lg mb-4">Follow Us</h3>
              <p className="text-cream/70 mb-3">Join us on our adventures</p>
              <div className="flex space-x-4 mb-6">
                <a 
                  href="https://www.facebook.com/profile.php?id=61587271117394" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-cream/10 hover:bg-cream/20 transition-colors"
                  aria-label="Follow us on Facebook"
                  data-testid="facebook-link"
                >
                  <Facebook size={20} className="text-cream" />
                </a>
                <a 
                  href="https://www.instagram.com/chimba_travel/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-cream/10 hover:bg-cream/20 transition-colors"
                  aria-label="Follow us on Instagram"
                  data-testid="instagram-link"
                >
                  <Instagram size={20} className="text-cream" />
                </a>
              </div>
              <h4 className="font-heading font-bold text-sm mb-2">Monthly Newsletter</h4>
              <p className="text-cream/70 text-xs mb-3">Travel inspiration & exclusive offers — once a month, no spam.</p>
              <NewsletterSignup source="footer" compact />
            </div>
          </div>
          
          <div className="border-t border-cream/15 mt-8 pt-8 text-center text-cream/60">
            <p>&copy; 2026 Chimba Travel. A division of Thom Kriner LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
